import React, { useMemo } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  const normalizeAllowed = useMemo(() => {
    const raw =
      user?.allowedModules ||
      user?.modules ||
      user?.access_modules ||
      user?.module_access ||
      user?.permissions ||
      [];

    if (!raw || (Array.isArray(raw) && raw.length === 0)) return null;

    const asArray = Array.isArray(raw) ? raw : Object.values(raw || {});
    const normalized = asArray
      .map((entry) => {
        if (typeof entry === 'string') return entry.toLowerCase();
        if (entry?.path) return String(entry.path).toLowerCase();
        if (entry?.label) return String(entry.label).toLowerCase();
        if (entry?.name) return String(entry.name).toLowerCase();
        return null;
      })
      .filter(Boolean);

    return normalized.length ? normalized : null;
  }, [user]);

  const isPathAllowed = (pathname) => {
    if (!normalizeAllowed) return true; // unrestricted
    const path = (pathname || '').toLowerCase();
    return normalizeAllowed.some((allowed) => allowed && path.startsWith(allowed));
  };

  if (isLoading) {
    // Show loading spinner while checking authentication
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login page with the intended destination
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isPathAllowed(location?.pathname)) {
    // If the current path is not allowed, redirect to the first allowed path or dashboard
    const fallback =
      normalizeAllowed?.find((p) => typeof p === 'string' && p.startsWith('/')) ||
      '/dashboard';
    return <Navigate to={fallback} replace />;
  }

  return children;
};

export default ProtectedRoute;